import TabPageHeading from '../components/Layout/TabPageHeading';
import React, { useState, useEffect } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useOrganizerPortal } from '../contexts/OrganizerPortalContext';
import { useAuth } from '../contexts/AuthContext';
import { EventEditResponse, EventEditStatus, EventEditType } from '@radevents/shared';
import eventService from '../services/eventService';
import toast from 'react-hot-toast';
import PublicHeader from '../components/Layout/PublicHeader';
import FeedbackWidget from '../components/FeedbackWidget';
import { Rueckfrage } from '../components/ui';
import { AccountDetailsFields, useAccountDetailsForm, useSaveAccountDetails } from '../components/AccountDetails';
import { accountDetailsRequest, challengeToday, ChallengeMe, ChallengeSummary } from '@radevents/shared';
import challengeService from '../services/challengeService';
import { ProfileChallenge } from '../components/winterchallenge/ProfileChallenge';
import ProfilUebersicht, { ProfilDaten } from '../components/community/ProfilUebersicht';
import communityService from '../services/communityService';
import { profilUebersicht } from '@radevents/shared';
import { Bell, Building2, ChevronDown, ChevronRight, Info, KeyRound, LayoutDashboard, Mail, Users, Copy, ListChecks, CalendarCog, LogOut, Trash2 } from 'lucide-react';
import { forgetChallengeMe } from '../components/WinterchallengePromoBanner';

const FONT_HEAD = "'Archivo',sans-serif";
const FONT_MONO = "'Space Mono',monospace";

export default function Profile() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin, isAdminOrModerator, logout, updateLocation, updateProfile } = useAuth();
  // Vereine, die dieses Konto pflegt — der Einstieg dorthin gehoert ins Profil.
  const { organizers: myOrganizers } = useOrganizerPortal();
  const [submissions, setSubmissions] = useState<EventEditResponse[]>([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
  });
  const [savingProfile, setSavingProfile] = useState(false);
  // Mit der Heimatregion (V191): sie steht auch in der Winterchallenge-Wertung.
  const detailsForm = useAccountDetailsForm(user);
  const saveDetails = useSaveAccountDetails();
  const [savingDetails, setSavingDetails] = useState(false);
  // Winterchallenge im Kopf, nur wenn das Konto mitmacht; ohne Saison oder Teilnahme bleibt er still.
  const [challenge, setChallenge] = useState<{ me: ChallengeMe; summary: ChallengeSummary } | null>(null);
  const [uebersicht, setUebersicht] = useState<ProfilDaten | null>(null);
  /** Welches Formular gerade aufgeklappt ist. Zu Beginn keins: das Profil ist zuerst eine Übersicht. */
  const [offen, setOffen] = useState<'daten' | 'ueber' | null>(null);
  const [abmeldenFrage, setAbmeldenFrage] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      setLoadingSubmissions(true);
      eventService.getMySubmissions()
        .then(setSubmissions)
        .catch(() => {})
        .finally(() => setLoadingSubmissions(false));
      Promise.all([challengeService.me(), challengeService.summary()])
        .then(([me, summary]) => setChallenge(me ? { me, summary } : null))
        .catch(() => setChallenge(null));
      Promise.all([communityService.planning('mine'), communityService.overview()])
        .then(([events, overview]) => setUebersicht(profilUebersicht(events, overview.people)))
        .catch(() => setUebersicht({ vorgemerkt: 0, dabei: 0, freunde: 0, anfragen: 0, naechstes: null }));
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: { pathname: '/profile' } }} replace />;
  }

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await updateProfile({
        firstName: profileForm.firstName || undefined,
        lastName: profileForm.lastName || undefined,
        email: profileForm.email || undefined,
      });
      toast.success('Profil gespeichert');
    } catch (err: any) {
      toast.error(err.message || 'Profil konnte nicht gespeichert werden');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveDetails = async () => {
    const { request, error } = accountDetailsRequest(detailsForm.birthYear, detailsForm.categories);
    if (!request) { toast.error(error ?? 'Angaben unvollständig'); return; }
    const location = detailsForm.location.trim();
    setSavingDetails(true);
    try {
      // Region zuerst: eine gewählte bleibt dann stehen, wenn der Standort gespeichert wird.
      await saveDetails(request, detailsForm.region);
      if (location && location !== (user?.location ?? '')) await updateLocation(location);
      toast.success('Angaben gespeichert');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Angaben konnten nicht gespeichert werden');
    } finally {
      setSavingDetails(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const roleLabel = user?.role === 'ADMIN' ? 'ADMIN' : user?.role === 'MODERATOR' ? 'MODERATOR' : 'NUTZER';
  const memberYear = user?.createdAt ? "'" + String(new Date(user.createdAt).getFullYear()).slice(2) : '—';

  const cardStyle: React.CSSProperties = {
    background: '#fff', border: '1px solid #E5E1E1', borderRadius: 16,
  };
  const monoLabel: React.CSSProperties = {
    fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '0.12em',
    textTransform: 'uppercase', color: '#9A9494',
  };
  // Wie die Felder unter „Über dich“ (`AccountDetailsFields`), damit beide Karten gleich aussehen.
  const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', marginTop: 6, background: '#fff', border: '1px solid #E5E1E1',
    borderRadius: 8, padding: '11px 12px', fontSize: 15, color: '#2C2C2C',
  };
  const fieldLabel: React.CSSProperties = {
    display: 'block', fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.1em',
    textTransform: 'uppercase', color: '#9A9494',
  };
  const smallBtn = (busy: boolean): React.CSSProperties => ({
    flexShrink: 0, background: '#922C57', color: '#fff', border: 'none',
    borderRadius: 8, padding: '11px 16px', fontWeight: 700, fontSize: 14,
    cursor: 'pointer', opacity: busy ? 0.6 : 1,
  });
  /** Eine Zeile der Listen „Konto“, „Mehr“ und „Administration“: Symbol, Text, Pfeil. */
  const zeile = (to: string, icon: React.ReactNode, label: string) => (
    <Link to={to} className="pf-zeile">
      <span className="pf-symbol">{icon}</span>
      <span className="pf-text">{label}</span>
      <ChevronRight size={18} className="pf-pfeil" aria-hidden />
    </Link>
  );
  const anzeigeName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.username;

  const statusChip = (status: EventEditStatus) => {
    const map: Record<string, { c: string; bg: string; l: string }> = {
      [EventEditStatus.PENDING]: { c: '#A82F5A', bg: 'rgba(168,47,90,0.12)', l: 'AUSSTEHEND' },
      [EventEditStatus.APPROVED]: { c: '#1F8A5B', bg: 'rgba(31,138,91,0.12)', l: 'GENEHMIGT' },
      [EventEditStatus.REJECTED]: { c: '#9A9494', bg: 'rgba(151,145,159,0.14)', l: 'ABGELEHNT' },
      [EventEditStatus.MERGED]: { c: '#6A6A6A', bg: 'rgba(151,145,159,0.14)', l: 'ZUSAMMENGEFÜHRT' },
    };
    const s = map[status] || map[EventEditStatus.PENDING];
    return (
      <span style={{
        flexShrink: 0, fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.06em',
        color: s.c, background: s.bg, padding: '5px 9px', borderRadius: 6,
      }}>{s.l}</span>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader />
      <div className="app-page tab-page-content" style={{ flex: 1, width: '100%', boxSizing: 'border-box', maxWidth: 820, margin: '0 auto', padding: '34px 40px 60px' }}>
        <div className="tab-page-heading"><TabPageHeading>Profil</TabPageHeading></div>

        {/* Hero card */}
        <div style={{
          position: 'relative', overflow: 'hidden', borderRadius: 18, padding: 26,
          background: 'linear-gradient(135deg,#3A2E34 0%,#922C57 135%)',
        }}>
          <div style={{
            position: 'absolute', right: -40, top: -40, width: 180, height: 180,
            background: '#C25078', opacity: 0.18, transform: 'rotate(45deg)', borderRadius: 34,
          }} />
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 18 }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', background: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: FONT_HEAD, fontWeight: 800, fontSize: 26, color: '#922C57', flexShrink: 0,
            }}>
              {(anzeigeName || user?.username || '?')[0]?.toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Vor- und Nachname dürfen umbrechen: abgeschnitten („Philipp Got…“) stand dort nur der halbe Name. */}
              <div style={{ fontFamily: FONT_HEAD, fontWeight: 800, fontSize: 22, lineHeight: 1.15, color: '#fff', overflowWrap: 'anywhere' }}>
                {anzeigeName}
              </div>
              <div style={{ fontSize: 14, color: '#E6DCE1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {anzeigeName !== user?.username ? `@${user?.username}` : user?.email}
              </div>
              <span style={{
                display: 'inline-block', marginTop: 6, fontFamily: FONT_MONO, fontSize: 10,
                letterSpacing: '0.1em', color: '#fff', background: 'rgba(255,255,255,0.18)',
                padding: '3px 9px', borderRadius: 6,
              }}>{roleLabel}</span>
            </div>
            {/* Oben rechts, damit die Zeile auch auf dem Handy nicht umbricht. */}
            <div style={{ alignSelf: 'flex-start', flexShrink: 0, textAlign: 'right' }}>
              <div style={{ fontFamily: FONT_HEAD, fontWeight: 800, fontSize: 24, color: '#fff' }}>{memberYear}</div>
              <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.08em', color: '#C9BCF0', marginTop: 2 }}>MITGLIED</div>
            </div>
          </div>
          {challenge && <ProfileChallenge me={challenge.me} summary={challenge.summary} today={challengeToday()}
            onSaved={next => { forgetChallengeMe(); setChallenge(c => (c ? { ...c, me: next } : c)); }} />}
        </div>

        <ProfilUebersicht daten={uebersicht} />

        {/* Links das Konto, rechts die Wege weiter (App.css `.profile-layout`); mobil untereinander.
            Die beiden Formulare stehen seit dem 30.10.2026 zugeklappt in der Liste „Konto“: auf
            dem Handy standen vorher elf Felder vor dem ersten Weg weiter. */}
        <div className="profile-layout">
          <div className="profile-col">
            <section className="pf-gruppe" aria-labelledby="pf-konto">
              <h2 id="pf-konto" className="pf-gruppe-titel">Konto</h2>

              <button type="button" className="pf-zeile" aria-expanded={offen === 'daten'} onClick={() => setOffen(offen === 'daten' ? null : 'daten')}>
                <span className="pf-symbol"><Users size={18} aria-hidden /></span>
                <span className="pf-text">Persönliche Daten<span className="pf-zusatz">{user?.email || 'Name und E-Mail'}</span></span>
                <ChevronDown size={18} className={offen === 'daten' ? 'pf-pfeil pf-pfeil--offen' : 'pf-pfeil'} aria-hidden />
              </button>
              {offen === 'daten' && (
                <div className="pf-formular ui-aufklappen">
                  <div style={{ display: 'flex', gap: 12 }}>
                    <label htmlFor="profile-first-name" style={{ flex: 1, minWidth: 0 }}>
                      <span style={fieldLabel}>Vorname</span>
                      <input id="profile-first-name" type="text" autoComplete="given-name" value={profileForm.firstName}
                        onChange={e => setProfileForm(f => ({ ...f, firstName: e.target.value }))} style={inputStyle} />
                    </label>
                    <label htmlFor="profile-last-name" style={{ flex: 1, minWidth: 0 }}>
                      <span style={fieldLabel}>Nachname</span>
                      <input id="profile-last-name" type="text" autoComplete="family-name" value={profileForm.lastName}
                        onChange={e => setProfileForm(f => ({ ...f, lastName: e.target.value }))} style={inputStyle} />
                    </label>
                  </div>
                  <label htmlFor="profile-email">
                    <span style={fieldLabel}>E-Mail</span>
                    <input id="profile-email" type="email" autoComplete="email" value={profileForm.email}
                      onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))} style={inputStyle} />
                  </label>
                  <div>
                    <button onClick={handleSaveProfile} disabled={savingProfile} style={smallBtn(savingProfile)}>
                      {savingProfile ? '…' : 'Speichern'}
                    </button>
                  </div>
                </div>
              )}

              {/* Freiwillige Angaben (V187), dieselben Felder wie im Fenster nach dem Anlegen */}
              <button type="button" className="pf-zeile" aria-expanded={offen === 'ueber'} onClick={() => setOffen(offen === 'ueber' ? null : 'ueber')}>
                <span className="pf-symbol"><CalendarCog size={18} aria-hidden /></span>
                <span className="pf-text">Über dich<span className="pf-zusatz">Was du fährst, Wohnort, Region</span></span>
                <ChevronDown size={18} className={offen === 'ueber' ? 'pf-pfeil pf-pfeil--offen' : 'pf-pfeil'} aria-hidden />
              </button>
              {offen === 'ueber' && (
                <div className="pf-formular ui-aufklappen">
                  <AccountDetailsFields
                    idPrefix="profile-details"
                    birthYear={detailsForm.birthYear} categories={detailsForm.categories}
                    onBirthYearChange={detailsForm.setBirthYear} onCategoriesChange={detailsForm.setCategories}
                    place={{ kind: 'region', value: detailsForm.region, onChange: detailsForm.setRegion }}
                    location={{ value: detailsForm.location, onChange: detailsForm.setLocation }}
                  />
                  <div>
                    <button onClick={handleSaveDetails} disabled={savingDetails} style={smallBtn(savingDetails)}>
                      {savingDetails ? '…' : 'Speichern'}
                    </button>
                  </div>
                </div>
              )}

              {zeile('/profile/notifications', <Bell size={18} aria-hidden />, 'Benachrichtigungen')}
              {zeile('/change-password', <KeyRound size={18} aria-hidden />, 'Passwort ändern')}
              {/* Abmelden und Konto löschen stehen seit dem 30.10.2026 hier statt ganz unten: dort
                  lagen sie direkt über der Tab-Leiste, ein Fehltipp auf „Events“ meldete ab. */}
              <button type="button" className="pf-zeile" onClick={() => setAbmeldenFrage(true)}>
                <span className="pf-symbol"><LogOut size={18} aria-hidden /></span>
                <span className="pf-text">Abmelden</span>
              </button>
              <Link to="/konto-loeschen" className="pf-zeile pf-zeile--gefahr">
                <span className="pf-symbol"><Trash2 size={18} aria-hidden /></span>
                <span className="pf-text">Konto löschen</span>
                <ChevronRight size={18} className="pf-pfeil" aria-hidden />
              </Link>
            </section>
          </div>

          <div className="profile-col">
            <section className="pf-gruppe" aria-labelledby="pf-mehr">
              <h2 id="pf-mehr" className="pf-gruppe-titel">Mehr</h2>
              {/* Fuer ein verknuepftes Konto ist das der EINZIGE sichtbare Weg in den
                  Veranstalter-Bereich: /profile laeuft ohne Layout, also ohne Seitenleiste. Deshalb
                  fuehrt der Eintrag direkt in den Bereich und nicht auf die oeffentliche Seite.
                  "Veranstalter" und nicht "Verein": dahinter steht genauso oft eine Firma. */}
              {myOrganizers.length > 0 && zeile('/veranstalter-bereich', <LayoutDashboard size={18} aria-hidden />,
                myOrganizers.length === 1 ? 'Mein Veranstalter-Bereich' : 'Meine Veranstalter')}
              {zeile('/veranstalter', <Building2 size={18} aria-hidden />, 'Veranstalter')}
              {/* Einstieg fuer Mobil: eine Fußzeile mit „Über uns“ und „Kontakt“ gibt es nicht mehr. */}
              {zeile('/ueber-uns', <Info size={18} aria-hidden />, 'Über uns')}
              {zeile('/kontakt', <Mail size={18} aria-hidden />, 'Kontakt')}
            </section>

            {isAdminOrModerator() && (
              <section className="pf-gruppe" aria-labelledby="pf-admin">
                <h2 id="pf-admin" className="pf-gruppe-titel">Administration</h2>
                {zeile('/admin/start', <LayoutDashboard size={18} aria-hidden />, 'Übersicht')}
                {zeile('/moderation/vorschlaege/events', <ListChecks size={18} aria-hidden />, 'Vorschläge prüfen')}
                {zeile('/moderation/duplikate/events', <Copy size={18} aria-hidden />, 'Duplikate')}
                {isAdmin() && zeile('/events/manage', <CalendarCog size={18} aria-hidden />, 'Events verwalten')}
              </section>
            )}
          </div>
        </div>

        {/* Meine Vorschläge */}
        {(loadingSubmissions || submissions.length > 0) && (
          <div style={{ ...cardStyle, padding: 20, marginTop: 18 }}>
            <div style={{ ...monoLabel, marginBottom: 12 }}>Meine Vorschläge</div>
            {loadingSubmissions ? (
              <div style={{ fontSize: 14, color: '#6A6A6A' }}>Laden…</div>
            ) : (
              submissions.map((sub, i) => (
                <div key={sub.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                  padding: i === 0 ? '0 0 13px' : '13px 0',
                  borderBottom: i < submissions.length - 1 ? '1px solid #EDE9E9' : 'none',
                  borderTop: i === 0 ? 'none' : undefined,
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#2C2C2C' }}>{sub.title}</div>
                    <div style={{ fontSize: 12, color: '#6A6A6A', marginTop: 2 }}>
                      {sub.type === EventEditType.CREATE ? 'Neues Event' : 'Bearbeitung'}
                      {' · '}
                      {sub.eventDate}
                    </div>
                  </div>
                  {statusChip(sub.status)}
                </div>
              ))
            )}
          </div>
        )}

        {/* Feedback widget — profile placement (matches the mobile app) */}
        <FeedbackWidget variant="app" style={{ marginTop: 18 }} />
        <Rueckfrage offen={abmeldenFrage} titel="Abmelden?"
          bestaetigen="Abmelden" onAbbrechen={() => setAbmeldenFrage(false)}
          onBestaetigen={() => { setAbmeldenFrage(false); void handleLogout(); }} />

      </div>
    </div>
  );
}
